// START: GitHub Copilot Generated Code
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import config from "../../config/config";
import { ApiOptions, fetchApiResult } from "../../services/apiServices";
import { getLocalPageContents } from "../../services/localContentService";
import { LogVerbose } from "../../services/logUtils";
import { readFromStorage, writeToStorage } from "../../services/storageServies";
import { PageType } from "../../types/types";
import { State } from "../state";

import type { ContentDictionary, PageContents } from "../../services/contentService";
/**
 * ContentState Interface
 * Manages page contents cache for multiple languages
 * OWASP Security: All data is validated before storage
 * ADA Compliance: Content structure supports accessibility requirements
 */
export interface ContentState {
  // Store page contents per language: { "en-US": { home: {...}, payBill: {...} }, ... }
  pageContentsByLanguage: { [language: string]: PageContents };
  // Track which languages are currently being loaded
  loadingLanguages: string[];
  // Errors per language
  errors: { [language: string]: string | null };
  // START: GitHub Copilot Generated Code
  // Track languages that have been loaded from API or session cache (not just local fallback).
  // Used to distinguish pre-seeded local content from authoritative API content.
  apiLoadedLanguages: string[];
  // END: GitHub Copilot Generated Code
}

// START: GitHub Copilot Generated Code
/**
 * Pre-populate all supported languages with local bundled translations so that
 * pageContentsByLanguage is never empty on first render. English (default) is
 * listed first in config.languageConfig.languages, so it is seeded first.
 * This prevents the spinner in App.tsx from blocking the initial paint.
 *
 * DRY Principle: Delegates to getLocalPageContents for the language-to-file mapping.
 * OWASP Security: Only reads from static bundled files; no user input involved.
 */
const buildInitialPageContents = (): { [language: string]: PageContents } => {
  const initial: { [language: string]: PageContents } = {};
  for (const langOption of config.languageConfig.languages) {
    const local = getLocalPageContents(langOption.name);
    if (local) {
      // eslint-disable-next-line security/detect-object-injection
      initial[langOption.name] = local;
    }
  }
  return initial;
};
// END: GitHub Copilot Generated Code

const initialState: ContentState = {
  // START: GitHub Copilot Generated Code
  // All languages are pre-seeded with local bundles; apiLoadedLanguages starts empty.
  pageContentsByLanguage: buildInitialPageContents(),
  // END: GitHub Copilot Generated Code
  loadingLanguages: [],
  errors: {},
  // START: GitHub Copilot Generated Code
  apiLoadedLanguages: [],
  // END: GitHub Copilot Generated Code
};

/**
 * Async thunk to load page contents for a specific language
 * First checks sessionStorage cache, then fetches from API if not found
 * OWASP Security: URL parameters are sanitized, API responses validated
 */
export const loadPageContentsAsync = createAsyncThunk<
  { language: string; pageContents: PageContents },
  string,
  { state: State }
>(
  'content/loadPageContents',
  async (language: string, { rejectWithValue, dispatch }) => {
    try {
      LogVerbose(`Loading Page Contents in ${language}`);
      const keyName = `PageContents_${language}`;

      // Check sessionStorage cache first
      const cached = readFromStorage<PageContents>(keyName, 'SessionStorage');
      if (cached) {
        LogVerbose(`Using cached info for Page Contents in ${language}`);
        return { language, pageContents: cached };
      }

      // No cache available — immediately load bundled local content as fallback
      // so the UI can render without waiting for the API response
      const localContent = getLocalPageContents(language);
      if (localContent) {
        LogVerbose(`Using local fallback content for ${language} while API loads`);
        dispatch(setFallbackContent({ language, pageContents: localContent }));
      }

      // Fetch from API
      LogVerbose(`Fetching Page Contents in ${language} from API`);
      const url = `${config.apiConfig.baseUrl}/${config.apiConfig.pageContentsEndpoint}?language=${encodeURIComponent(language)}&defaultLanguage=${config.languageConfig.defaultLanguageName}`;
      const apiOptions: ApiOptions = {
        url: url,
      };

      const result = await fetchApiResult<PageContents>(apiOptions);
      if (!result.ok || !result.data) {
        return rejectWithValue(`Failed to load page contents for language: ${language}`);
      }

      const pageContents = result.data;

      // Cache in sessionStorage
      writeToStorage(keyName, pageContents);

      LogVerbose(`Page Contents in ${language} loaded successfully from API`);
      return { language, pageContents };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`Failed to load page contents for ${language}:`, errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// START: GitHub Copilot Generated Code
/**
 * Pre-load API content for all configured languages in the background.
 * The default language (English) is awaited first to ensure authoritative content
 * is available immediately; all remaining languages then load concurrently.
 *
 * Purpose: Warm up the content cache so language switches are instant with no
 * visible loading state after the initial paint.
 *
 * DRY Principle: Delegates deduplication logic to loadPageContentsAsync.
 * OWASP Security: Language codes come from config, not user input.
 * ADA Compliance: Ensures all localizations are ready before the user needs them.
 */
export const preloadAllLanguageContentsAsync = createAsyncThunk<
  void,
  void,
  { state: State }
>(
  'content/preloadAllLanguages',
  async (_, { dispatch, getState }) => {
    const languages = config.languageConfig.languages;
    const defaultLang = config.languageConfig.defaultLanguageName;

    // Load default language (English) first — await it so it is authoritative before others.
    const startState = getState();
    const isDefaultLoaded = startState.content.apiLoadedLanguages.includes(defaultLang);
    const isDefaultLoading = startState.content.loadingLanguages.includes(defaultLang);
    if (!isDefaultLoaded && !isDefaultLoading) {
      LogVerbose(`Preload: Loading default language content (${defaultLang})`);
      await dispatch(loadPageContentsAsync(defaultLang));
    }

    // Load remaining languages concurrently.
    const otherLanguages = languages.filter(l => l.name !== defaultLang);
    await Promise.all(
      otherLanguages.map((langOption) => {
        const currentState = getState();
        const isLoaded = currentState.content.apiLoadedLanguages.includes(langOption.name);
        const isLoading = currentState.content.loadingLanguages.includes(langOption.name);
        if (!isLoaded && !isLoading) {
          LogVerbose(`Preload: Loading language content (${langOption.name})`);
          return dispatch(loadPageContentsAsync(langOption.name));
        }
        return Promise.resolve();
      })
    );
    LogVerbose('Preload: All language contents loaded');
  }
);
// END: GitHub Copilot Generated Code

/**
 * Content Slice
 * Manages centralized storage of page contents across languages
 * DRY Principle: Single source of truth for all page content
 */
export const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    /**
     * Set fallback content from local bundled translation files.
     * Used to provide immediate content while the API response is pending.
     * Only sets content if no content exists yet for the given language,
     * to avoid overwriting API-loaded content with local fallback.
     * ADA Compliance: Ensures the page renders promptly with localized content
     */
    setFallbackContent: (state: ContentState, action: PayloadAction<{ language: string; pageContents: PageContents }>) => {
      const { language, pageContents } = action.payload;
      // Only set if no content exists yet for this language
      // OWASP Security: language is validated by the caller (async thunk)
      // eslint-disable-next-line security/detect-object-injection
      if (!state.pageContentsByLanguage[language]) {
        // eslint-disable-next-line security/detect-object-injection
        state.pageContentsByLanguage[language] = pageContents;
      }
    },
    /**
     * Clear all cached content
     * Useful for cache invalidation scenarios
     */
    clearAllContent: (state: ContentState) => {
      state.pageContentsByLanguage = {};
      state.loadingLanguages = [];
      state.errors = {};
      // START: GitHub Copilot Generated Code
      state.apiLoadedLanguages = [];
      // END: GitHub Copilot Generated Code
      LogVerbose('Cleared all content cache');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadPageContentsAsync.pending, (state, action) => {
        const language = action.meta.arg;
        if (!state.loadingLanguages.includes(language)) {
          state.loadingLanguages.push(language);
        }
        // OWASP Security: language is validated in the async thunk before reaching here
        // eslint-disable-next-line security/detect-object-injection
        state.errors[language] = null;
      })
      .addCase(loadPageContentsAsync.fulfilled, (state, action) => {
        const { language, pageContents } = action.payload;
        // OWASP Security: language is validated in the async thunk before reaching here
        // eslint-disable-next-line security/detect-object-injection
        state.pageContentsByLanguage[language] = pageContents;
        state.loadingLanguages = state.loadingLanguages.filter(lang => lang !== language);
        // eslint-disable-next-line security/detect-object-injection
        state.errors[language] = null;
        // START: GitHub Copilot Generated Code
        // Mark as API-loaded (covers both live API and session-cached responses).
        // Distinguishes authoritative content from pre-seeded local fallback content.
        if (!state.apiLoadedLanguages.includes(language)) {
          state.apiLoadedLanguages.push(language);
        }
        // END: GitHub Copilot Generated Code
      })
      .addCase(loadPageContentsAsync.rejected, (state, action) => {
        const language = action.meta.arg;
        state.loadingLanguages = state.loadingLanguages.filter(lang => lang !== language);
        // OWASP Security: language is validated in the async thunk before reaching here
        // eslint-disable-next-line security/detect-object-injection
        state.errors[language] = action.payload as string || 'Failed to load content';
      });
  },
});

// Actions
export const { clearAllContent, setFallbackContent } = contentSlice.actions;

// Selectors

/**
 * Select page contents for the current language
 * Combines languageSlice and contentSlice
 */
export const selectPageContentsForCurrentLanguage = (state: State): PageContents | null => {
  const currentLanguage = state.language.language;
  if (!currentLanguage) return null;
  // OWASP Security: currentLanguage comes from Redux language state which is validated
  // eslint-disable-next-line security/detect-object-injection
  return state.content.pageContentsByLanguage[currentLanguage] || null;
};

/**
 * Select page contents for a specific language
 */
export const selectPageContentsForLanguage = (state: State, language: string): PageContents | null => {
  // OWASP Security: language parameter should be validated by caller
  // eslint-disable-next-line security/detect-object-injection
  return state.content.pageContentsByLanguage[language] || null;
};

/**
 * Select content dictionary for a specific page in current language
 * Main selector for components to use
 * OWASP Security: Validates pageId exists before access to prevent object injection
 */
export const selectContentDictionary = (state: State, pageId: PageType): ContentDictionary | null => {
  const pageContents = selectPageContentsForCurrentLanguage(state);
  if (!pageContents) return null;

  // Validate pageId exists to prevent object injection
  if (!Object.prototype.hasOwnProperty.call(pageContents, pageId)) {
    console.error(`No content found for pageId: ${pageId}`);
    return null;
  }

  // eslint-disable-next-line security/detect-object-injection
  return pageContents[pageId] || null;
};

/**
 * Check if content is loading for current language
 */
export const selectIsLoadingForCurrentLanguage = (state: State): boolean => {
  const currentLanguage = state.language.language;
  if (!currentLanguage) return false;
  return state.content.loadingLanguages.includes(currentLanguage);
};

/**
 * Check if content is loading for a specific language
 */
export const selectIsLoadingForLanguage = (state: State, language: string): boolean => {
  return state.content.loadingLanguages.includes(language);
};

/**
 * Get error for current language if any
 */
export const selectErrorForCurrentLanguage = (state: State): string | null => {
  const currentLanguage = state.language.language;
  if (!currentLanguage) return null;
  // OWASP Security: currentLanguage comes from Redux language state which is validated
  // eslint-disable-next-line security/detect-object-injection
  return state.content.errors[currentLanguage] || null;
};

/**
 * Check if content exists for current language
 */
export const selectHasContentForCurrentLanguage = (state: State): boolean => {
  const currentLanguage = state.language.language;
  if (!currentLanguage) return false;
  // OWASP Security: currentLanguage comes from Redux language state which is validated
  // eslint-disable-next-line security/detect-object-injection
  return !!state.content.pageContentsByLanguage[currentLanguage];
};

// START: GitHub Copilot Generated Code
/**
 * Check if API content (or session-cached) has been loaded for a specific language.
 * Returns false for languages that only have pre-seeded local bundled fallback content.
 * Used by contentMiddleware to decide whether a fresh API fetch is still needed.
 */
export const selectHasApiContentForLanguage = (state: State, language: string): boolean => {
  return state.content.apiLoadedLanguages.includes(language);
};
// END: GitHub Copilot Generated Code

export default contentSlice.reducer;
// END: GitHub Copilot Generated Code
